import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AgreementCommentPayloadItem,
  sendQuestionnaireLink,
  rejectAgreement,
  confirmAgreement,
  commentAgreement,
  completeAgreement,
  updateAgreementContent,
  updateAgreementData,
  updateAgreementStatements,
  updateAgreementTaxStatus,
  deleteAgreement,
} from "@/services/agreements";
import { ReadAgreement } from "@/types/agreement";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { Module, PATH_AGREEMENTS } from "@/constants";
import { AgreementHandlerKey, CommentItem } from "../context/AgreementContext";

interface UseAgreementHandlersProps {
  agreementId: string;
  commentTexts: CommentItem[];
  openDialog: (handlerKey: AgreementHandlerKey, onConfirm: () => void) => void;
  closeDialog: () => void;
  onCommentSuccess?: () => void;
}

export const useAgreementHandlers = ({
  agreementId,
  commentTexts,
  openDialog,
  closeDialog,
  onCommentSuccess,
}: UseAgreementHandlersProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const sendQuestionnaireMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => sendQuestionnaireLink(agreementId),
    errorTitle: t("agreements.errors.sendQuestionnaire"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.questionnaireSent"),
      });
      closeDialog();
    },
  });

  const rejectAgreementMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => rejectAgreement(agreementId),
    errorTitle: t("agreements.errors.reject"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.rejected"),
      });
      closeDialog();
    },
  });

  const confirmAgreementMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => confirmAgreement(agreementId),
    errorTitle: t("agreements.errors.confirm"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.confirmed"),
      });
      closeDialog();
    },
  });

  const commentAgreementMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    AgreementCommentPayloadItem[],
    unknown
  >({
    mutationFn: (comments) => commentAgreement(agreementId, comments),
    errorTitle: t("agreements.errors.comment"),
    onSuccess: () => {
      onCommentSuccess?.();
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.commented"),
      });
      closeDialog();
    },
  });

  const completeAgreementMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    { end_date: string },
    unknown
  >({
    mutationFn: ({ end_date }) => completeAgreement(agreementId, { end_date }),
    errorTitle: t("agreements.errors.complete"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.completed"),
      });
      closeDialog();
    },
  });

  const updateContentMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >({
    mutationFn: ({ amendment_date }) =>
      updateAgreementContent(agreementId, amendment_date),
    errorTitle: t("agreements.errors.updateContent"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.contentUpdated"),
      });
      closeDialog();
    },
  });

  const updateDataMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >({
    mutationFn: ({ amendment_date }) =>
      updateAgreementData(agreementId, { amendment_date }),
    errorTitle: t("agreements.errors.updateData"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.dataUpdated"),
      });
      closeDialog();
    },
  });

  const updateStatementsMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => updateAgreementStatements(agreementId),
    errorTitle: t("agreements.errors.updateStatements"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.statementsUpdated"),
      });
      closeDialog();
    },
  });

  const updateTaxStatusMutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    { amendment_date: string },
    unknown
  >({
    mutationFn: ({ amendment_date }) =>
      updateAgreementTaxStatus(agreementId, { amendment_date }),
    errorTitle: t("agreements.errors.updateTaxStatus"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements, agreementId],
      });
      toast({
        title: t("agreements.success.taxStatusUpdated"),
      });
      closeDialog();
    },
  });

  const deleteAgreementMutation = useMutationWithErrorHandling<
    unknown,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => deleteAgreement(agreementId),
    errorTitle: t("agreements.errors.delete"),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [Module.Agreements, agreementId] });
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      toast({
        title: t("agreements.success.delete"),
      });
      navigate(PATH_AGREEMENTS);
    },
  });

  const handleSendQuestionnaire = useCallback(() => {
    openDialog("sendQuestionnaire", () =>
      sendQuestionnaireMutation.mutate(undefined),
    );
  }, [openDialog, sendQuestionnaireMutation]);

  const handleReject = useCallback(() => {
    openDialog("reject", () => rejectAgreementMutation.mutate(undefined));
  }, [openDialog, rejectAgreementMutation]);

  const handleConfirm = useCallback(() => {
    openDialog("confirm", () => confirmAgreementMutation.mutate(undefined));
  }, [openDialog, confirmAgreementMutation]);

  const handleComment = useCallback(
    async (comments?: CommentItem[]) => {
      const validComments = (
        comments
          ? comments.map((commentItem) => ({
              comment: commentItem.text.trim(),
              created_at: commentItem.createdAt,
            }))
          : commentTexts.map((c) => ({
              comment: c.text.trim(),
              created_at: c.createdAt,
            }))
      ).filter((item) => item.comment.length > 0);

      if (validComments.length === 0) {
        return;
      }

      await commentAgreementMutation.mutateAsync(validComments);
    },
    [commentAgreementMutation, commentTexts],
  );

  const handleComplete = useCallback(
    (end_date: string) => {
      completeAgreementMutation.mutate({ end_date });
    },
    [completeAgreementMutation],
  );

  const handleUpdateContent = useCallback(
    (amendment_date: string) => {
      updateContentMutation.mutate({ amendment_date });
    },
    [updateContentMutation],
  );

  const handleUpdateData = useCallback(
    (amendment_date: string) => {
      updateDataMutation.mutate({ amendment_date });
    },
    [updateDataMutation],
  );

  const handleUpdateStatements = useCallback(() => {
    openDialog("updateStatements", () =>
      updateStatementsMutation.mutate(undefined),
    );
  }, [openDialog, updateStatementsMutation]);

  const handleUpdateTaxStatus = useCallback(
    (amendment_date: string) => {
      updateTaxStatusMutation.mutate({ amendment_date });
    },
    [updateTaxStatusMutation],
  );

  const handleDelete = useCallback(() => {
    openDialog("delete", () => deleteAgreementMutation.mutate(undefined));
  }, [openDialog, deleteAgreementMutation]);

  const clearError = useCallback(() => {
    sendQuestionnaireMutation.reset();
    rejectAgreementMutation.reset();
    confirmAgreementMutation.reset();
    commentAgreementMutation.reset();
    completeAgreementMutation.reset();
    updateContentMutation.reset();
    updateDataMutation.reset();
    updateStatementsMutation.reset();
    updateTaxStatusMutation.reset();
    deleteAgreementMutation.reset();
  }, [
    sendQuestionnaireMutation,
    rejectAgreementMutation,
    confirmAgreementMutation,
    commentAgreementMutation,
    completeAgreementMutation,
    updateContentMutation,
    updateDataMutation,
    updateStatementsMutation,
    updateTaxStatusMutation,
    deleteAgreementMutation,
  ]);

  return {
    handleSendQuestionnaire,
    handleComplete,
    handleReject,
    handleConfirm,
    handleComment,
    handleUpdateContent,
    handleUpdateData,
    handleUpdateStatements,
    handleUpdateTaxStatus,
    handleDelete,
    clearError,
    mutations: {
      sendQuestionnaireMutation,
      rejectAgreementMutation,
      confirmAgreementMutation,
      commentAgreementMutation,
      completeAgreementMutation,
      updateContentMutation,
      updateDataMutation,
      updateStatementsMutation,
      updateTaxStatusMutation,
      deleteAgreementMutation,
    },
  };
};
